import { useState, useRef, useEffect } from 'react';

import { useEventCallback } from '../../useEventCallback/useEventCallback';
import type { TestTaker } from '../../../interface/interface';
import type { Prompt } from '../../../interface/Prompt';
import type { IntentData } from '../../../interface/IntentData';
import { IntentMetadata } from '../../useAudioRecorder/useAudioRecorder';
import { pushFaroEvent } from '../../../faroSession';

interface Options {
    testTaker: TestTaker;
    currentPrompt: Prompt;
    recordedChunks: Float32Array[];
    onIntentReceived: (intent: IntentData) => void;
    testEnded: boolean;
    Websocket: React.MutableRefObject<WebSocket | null | undefined>;
}

export const useIntentDetection = (props: Options) => {
    const { onIntentReceived, currentPrompt, testEnded, Websocket } = props;
    const [error, setError] = useState<string | null>(null);

    const intentDetectionActiveRef = useRef(false);
    const intentStartIndexRef = useRef(0);
    const intentUsedRef = useRef(false);
    const intentTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const initialSpeechEndedRef = useRef(false);
    const lastIntentPromptRef = useRef<Prompt>();
    const currentPromptRef = useRef<Prompt>();

    const _onIntentReceived = useEventCallback(onIntentReceived);

    const resetIntentDetection = () => {
        clearTimeout(intentTimeoutRef.current);
        intentStartIndexRef.current = 0;
        intentUsedRef.current = false;
        initialSpeechEndedRef.current = false;
    };

    const handleMessage = (event: any) => {
        if (event.data === 'pong') return;

        const ws = Websocket.current;
        try {
            const data: IntentData = JSON.parse(event.data);
            pushFaroEvent('intent_websocket_message_received');

            if (data) {
                if (ws?.readyState === WebSocket.OPEN) {
                    const metadata: IntentMetadata = {
                        type: 'FLAG_UPDATE',
                        message: {
                            detectIntent: false,
                        }
                    };
                    ws.send(JSON.stringify(metadata));
                    intentUsedRef.current = true;
                }

                const testMovedOn = lastIntentPromptRef.current !== currentPromptRef.current;
        
                if (testMovedOn) {
                    pushFaroEvent('intent_result_ignored', { reason: 'test_moved_on' });
                    return;
                }

                if (intentDetectionActiveRef.current) {
                    pushFaroEvent('intent_data_received', { intent: data.intent });
                    _onIntentReceived?.(data);
                }
                intentDetectionActiveRef.current = false;
            }
        } catch (error) {
            pushFaroEvent('intent_parse_error', { error: String(error) });
        }
    };

     const handleOpen = () => {
        const url = Websocket.current?.url;
        pushFaroEvent('audio_websocket_opened', url ? { url } : undefined);
    };
    const handleError = () => {
        console.error('Audio Web socket is having error');
        setError('V110');
    };
     const handleClose = () => {
        const url = Websocket.current?.url;
        pushFaroEvent('audio_websocket_closed', url ? { url } : undefined);
    };

    useEffect(() => {
        if (!testEnded && Websocket.current) {
            const ws = Websocket.current;

            ws.addEventListener('message', handleMessage);
            ws.addEventListener('open', handleOpen);
            ws.addEventListener('error', handleError);
            ws.addEventListener('close', handleClose);
        }

        return () => {
            Websocket.current?.removeEventListener('message', handleMessage);
            Websocket.current?.removeEventListener('open', handleOpen);
            Websocket.current?.removeEventListener('error', handleError);
            Websocket.current?.removeEventListener('close', handleClose);
        };
    }, [testEnded, Websocket.current]);

    useEffect(() => {
        if (!currentPrompt) return;

        currentPromptRef.current = currentPrompt;

        return () => resetIntentDetection();
    }, [currentPrompt]);

    return {
        error,
        resetIntentDetection,
        intentUsedRef,
        intentDetectionActiveRef,
        initialSpeechEndedRef,
        lastIntentPromptRef,
        currentPromptRef,
    };
};

export const triggerIntentDetection = (Websocket?: WebSocket, enabled: boolean = true, cb?: () => void) => {
    if (Websocket?.readyState === WebSocket.OPEN) {
        cb?.();
        const payload: IntentMetadata = { 
            type: 'FLAG_UPDATE',
            message: { 
                detectIntent: enabled,
            }
        };
        Websocket.send(JSON.stringify(payload));
    }
};
